import { useApolloClient } from "@apollo/client";
import { Alert, Button, Table, Tooltip, Upload, Spin } from "antd";
import { SvgIcon } from "assets/icons";
import CustomModal from "components/CustomModal";
import { useFlags } from "flagsmith/react";
import { PREVIEW_PRODUCT_SOLD_DISPLAY_IMPORT } from "graphql/product/query";
import { useUploadFileProductPriceLogs, useGetImportFileProduct } from "hooks/product/product";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { notify } from "utils/helperFuncs";
import { readProductSoldDisplayExcel } from "./productSoldDisplayExcel";
import "./index.scss";

const displayMode = (mode) => (mode === "show" ? "Hiện" : "Ẩn");
const errorColumns = [
  { title: "Dòng", dataIndex: "row", width: 70 },
  { title: "Mã SP", dataIndex: "product_code" },
  { title: "Lý do", dataIndex: "reason" },
];
const previewColumns = [
  ...errorColumns.slice(0, 2),
  {
    title: "Hiển thị lượt bán",
    render: (_, item) =>
      item.display_mode_unchanged
        ? `${displayMode(item.old_display_mode)} (giữ nguyên)`
        : `${displayMode(item.old_display_mode)} → ${displayMode(item.new_display_mode)}`,
  },
  { title: "Lượt bán bổ sung", render: (_, item) => `${item.old_extra_sold_count} → ${item.new_extra_sold_count}` },
];
const SOLD_DISPLAY_HEADERS = ["Hiển thị lượt bán", "Lượt bán bổ sung"];
const LEGACY_FILE_MESSAGE =
  "File không có hai cột lượt bán: chỉ cập nhật các cột cũ, lượt bán của sản phẩm được giữ nguyên. Bấm Xác nhận để import.";
const importInvalid = (error) =>
  (error?.graphQLErrors || error?.errors || error?.networkError?.result?.errors || []).find(
    (item) => item.extensions?.code === "PRODUCT_IMPORT_INVALID"
  );
// A file-level PRODUCT_IMPORT_INVALID (e.g. one sold-display header missing) has a message but no row errors.
const invalidPreview = (error) => {
  const invalid = importInvalid(error);
  if (!invalid) return null;
  return invalid.extensions.errors
    ? { valid: false, items: [], errors: invalid.extensions.errors }
    : { valid: false, items: [], errors: [], message: invalid.message };
};

const CustomModalImportPriceSeries = ({ canUpdate, canUpdatePrice }) => {
  const client = useApolloClient();
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [columnTitles, setColumnTitles] = useState([]);
  const restrictedColumns =
    !canUpdatePrice &&
    columnTitles.some(
      (title) => title && !["Mã sản phẩm", "Mã SP", "Hiển thị lượt bán", "Lượt bán bổ sung"].includes(title)
    );
  const [reading, setReading] = useState(false);
  const [writing, setWriting] = useState(false);
  const [legacyError, setLegacyError] = useState(null);
  const request = useRef(0);
  const writeLock = useRef(false);
  const { handleExportFileProduct } = useGetImportFileProduct();
  const { handleImportProductPrices } = useUploadFileProductPriceLogs();
  const flags = useFlags(["is_disable_template_download_price_product"]);
  const showSample = !flags.is_disable_template_download_price_product.enabled;
  useEffect(
    () => () => {
      ++request.current;
    },
    []
  );

  const reset = () => {
    ++request.current;
    setFile(null);
    setColumnTitles([]);
    setPreview(null);
    setLegacyError(null);
    setReading(false);
  };
  const cancel = () => {
    if (!writeLock.current) {
      reset();
      setVisible(false);
    }
  };
  const chooseFile = async (nextFile) => {
    if (!canUpdate || writeLock.current) return;
    const version = ++request.current;
    setFile(nextFile);
    setColumnTitles([]);
    setPreview(null);
    setLegacyError(null);
    setReading(true);
    try {
      const variables = await readProductSoldDisplayExcel(nextFile);
      if (version !== request.current) return;
      setColumnTitles(variables.column_titles);
      // Legacy templates without both headers keep sold-display untouched, so BE expects no preview for them.
      if (!SOLD_DISPLAY_HEADERS.some((title) => variables.column_titles.includes(title))) {
        setPreview({ valid: true, items: [], errors: [], legacy: true });
        return;
      }
      const { data } = await client.query({
        query: PREVIEW_PRODUCT_SOLD_DISPLAY_IMPORT,
        variables,
        fetchPolicy: "no-cache",
        errorPolicy: "none",
      });
      if (version === request.current) setPreview(data.previewProductSoldDisplayImport);
    } catch (error) {
      if (version === request.current) {
        const invalid = invalidPreview(error);
        if (invalid) setPreview(invalid);
        else notify.error({ message: error.message });
      }
    } finally {
      if (version === request.current) setReading(false);
    }
  };
  const confirm = async () => {
    if (
      !canUpdate ||
      restrictedColumns ||
      writeLock.current ||
      reading ||
      !preview?.valid ||
      preview.errors.length ||
      !file
    )
      return;
    writeLock.current = true;
    setWriting(true);
    try {
      const response = await handleImportProductPrices(file);
      const result = response.data.product.importProductPrices;
      if (result.status === "SUCCEED") {
        notify.success({ message: `✅ Đã cập nhật ${result.updated_count} sản phẩm` });
        reset();
        setVisible(false);
      } else {
        setLegacyError(result);
        setPreview(null);
      }
    } catch (error) {
      const invalid = invalidPreview(error);
      if (invalid) setPreview(invalid);
      else notify.error({ message: error.message });
    } finally {
      writeLock.current = false;
      setWriting(false);
    }
  };
  const downloadSample = async () => {
    try {
      const response = await handleExportFileProduct({ filters: { ids: [""], isExample: true } });
      window.open(response.data.product.getImportFile.fileURL);
    } catch (error) {
      notify.error({ message: error.message });
    }
  };
  if (!canUpdate) return null;
  const hasErrors = preview && (!preview.valid || preview.errors.length > 0);
  const errorsCount = new Set(preview?.errors.map((item) => item.row)).size;
  return (
    <CustomModal
      className="modal-import-price-series"
      width={900}
      closable={false}
      maskClosable={!writing}
      keyboard={!writing}
      visible={visible}
      onOpen={() => {
        reset();
        setVisible(true);
      }}
      onCancel={cancel}
      footer={null}
      title={
        <span className="title">
          {legacyError ? t("product.errorDownFileInfoProduct") : t("product.downFileInfoProduct")}
        </span>
      }
      hideCancelButton
      hideConfirmButton
      addMoreButton={
        <>
          <Button disabled={writing} onClick={cancel}>
            {legacyError ? "Đóng" : "Hủy"}
          </Button>
          {legacyError ? (
            legacyError.errorFileURL ? (
              <a
                href={legacyError.errorFileURL}
                target="_blank"
                rel="noopener noreferrer nofollow"
                download="product-errors.xlsx"
              >
                {t("product.downFileError")}
              </a>
            ) : (
              <Button type="primary" onClick={reset}>
                Tải file khác
              </Button>
            )
          ) : (
            <Button
              type="primary"
              htmlType="button"
              disabled={!preview?.valid || !!hasErrors || restrictedColumns || reading || writing}
              loading={writing}
              onClick={confirm}
            >
              Xác nhận
            </Button>
          )}
        </>
      }
      customComponent={
        <Tooltip title="Tải file thông tin sản phẩm" color="#5B6673" placement="bottom">
          <Button type="text" aria-label="Import sản phẩm" icon={<SvgIcon.ImportPriceSeries style={{ width: 20 }} />} />
        </Tooltip>
      }
    >
      {!canUpdatePrice && (
        <Alert
          type="info"
          showIcon
          message="Bạn chỉ có thể cập nhật hai cột lượt bán: Hiển thị lượt bán và Lượt bán bổ sung. File chỉ được gồm Mã sản phẩm (hoặc Mã SP) và hai cột này; hãy xóa các cột cũ khỏi file trước khi import."
          style={{ marginBottom: 12 }}
        />
      )}
      {legacyError ? (
        <p>{legacyError.errorReason || t("serialIE.productInfo.errorReason")}</p>
      ) : (
        <>
          <Upload.Dragger
            name="file"
            multiple={false}
            maxCount={1}
            accept=".xlsx"
            disabled={writing}
            beforeUpload={() => false}
            fileList={
              file ? [{ uid: file.uid || file.name, name: file.name, status: "done", originFileObj: file }] : []
            }
            onRemove={() => {
              if (writeLock.current) return false;
              reset();
              return true;
            }}
            onChange={(info) => {
              if (info.file.status !== "removed") chooseFile(info.file.originFileObj || info.file);
            }}
          >
            <p className="ant-upload-drag-icon">
              <SvgIcon.LogoFileWordIcon />
            </p>
            <p className="ant-upload-text">{t("product.drapDropFile")}</p>
          </Upload.Dragger>
          {reading && <Spin tip="Đang xem trước file..." style={{ display: "block", margin: 12 }} />}
          {preview && (
            <div style={{ marginTop: 16 }}>
              <Alert
                type={hasErrors ? "error" : restrictedColumns ? "warning" : "success"}
                showIcon
                message={
                  preview.message ||
                  (hasErrors
                    ? `File có ${errorsCount} dòng lỗi. Không sản phẩm nào được cập nhật. Sửa file rồi import lại.`
                    : restrictedColumns
                      ? "File có các cột ngoài phạm vi lượt bán. Chỉ giữ Mã sản phẩm (hoặc Mã SP), Hiển thị lượt bán và Lượt bán bổ sung rồi import lại."
                      : preview.legacy
                        ? LEGACY_FILE_MESSAGE
                        : `${preview.items.length} sản phẩm sẽ được cập nhật. Kiểm tra lại rồi bấm Xác nhận.`)
                }
              />
              {!preview.legacy && !preview.message && (
                <Table
                  size="small"
                  columns={hasErrors ? errorColumns : previewColumns}
                  dataSource={hasErrors ? preview.errors : preview.items}
                  rowKey={(item, index) => `${item.row}-${index}`}
                  pagination={false}
                  scroll={{ y: 320 }}
                  style={{ marginTop: 12 }}
                />
              )}
            </div>
          )}
          <p className="text-info-file">{t("product.textInfoFile")}</p>
          {showSample && (
            <p className="text-download-file">
              {t("product.textDownFile")}{" "}
              <Button type="link" size="small" onClick={downloadSample}>
                {t("product.here")}
              </Button>
            </p>
          )}
        </>
      )}
    </CustomModal>
  );
};
export default CustomModalImportPriceSeries;
